// client/src/modules/fieldService/routes.tsx
import React from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import { FormConsumerProvider } from '../../contexts/FormConsumerContext';
import { lazyWithRetry } from '../../lib/lazyWithRetry';
import { guardPage } from '../pageGuard';
import { BILL_VIEW_PAGES } from '../pageAccess';
import { isOfficeContractReader } from '../../config/contractAccess';

const FieldServiceDashboard = lazyWithRetry(() => import('../../pages/FieldService/FieldServiceDashboard'));
const TicketsPage = lazyWithRetry(() => import('../../pages/FieldService/TicketsPage'));
const ServiceTicketDetail = lazyWithRetry(() => import('../../pages/FieldService/ServiceTicketDetail'));
const GeneratorsPage = lazyWithRetry(() => import('../../pages/FieldService/GeneratorsPage'));
const GeneratorDetail = lazyWithRetry(() => import('../../pages/FieldService/GeneratorDetail'));
const RecurringProgramHealth = lazyWithRetry(() => import('../../pages/FieldService/RecurringProgramHealth'));
const RecurringScheduleDetail = lazyWithRetry(() => import('../../pages/FieldService/RecurringScheduleDetail'));
const ContractsLibraryPage = lazyWithRetry(() => import('../../pages/FieldService/ContractsLibraryPage'));
const ContractDetailPage = lazyWithRetry(() => import('../../pages/FieldService/ContractDetailPage'));
const ContractExtractPage = lazyWithRetry(() => import('../../pages/FieldService/ContractExtractPage'));
const FSRReviewQueue = lazyWithRetry(() => import('../../pages/FieldService/FSRReviewQueue'));
const RecurringReviewPage = lazyWithRetry(() => import('../../pages/FieldService/RecurringReviewPage'));
const FollowUpReviewPage = lazyWithRetry(() => import('../../pages/FieldService/FollowUpReviewPage'));
const PinSuggestionsPage = lazyWithRetry(() => import('../../pages/FieldService/PinSuggestionsPage'));
const GeneratorDuplicatesPage = lazyWithRetry(() => import('../../pages/FieldService/GeneratorDuplicatesPage'));
const NeedsAttentionPage = lazyWithRetry(() => import('../../pages/FieldService/NeedsAttentionPage'));
const ServiceBillingStatus = lazyWithRetry(() => import('../../pages/FieldService/ServiceBillingStatus'));
const TicketOwnersPage = lazyWithRetry(() => import('../../pages/FieldService/TicketOwnersPage'));
const FSRReportPage = lazyWithRetry(() => import('../../pages/FieldService/FSRReportPage'));
const FieldServiceSchedulerPage = lazyWithRetry(() => import('../../pages/FieldService/FieldServiceSchedulerPage'));
const ScheduleBlockDetail = lazyWithRetry(() => import('../../pages/FieldService/ScheduleBlockDetail'));
const PartsCatalogDetail = lazyWithRetry(() => import('../../pages/FieldService/PartsCatalogDetail'));
const ServiceFileDetail = lazyWithRetry(() => import('../../pages/FieldService/ServiceFileDetail'));
const ServiceWritingPage = lazyWithRetry(() => import('../../pages/FieldService/ServiceWritingPage'));
const ArchivedFormPage = lazyWithRetry(() => import('../../pages/FieldService/ArchivedFormPage'));
const PlateCheckPage = lazyWithRetry(() => import('../../pages/FieldService/PlateCheckPage'));
const PlateSettlePage = lazyWithRetry(() => import('../../pages/FieldService/PlateSettlePage'));
const SampleIntakeDesk = lazyWithRetry(() => import('../../pages/FieldService/SampleIntakeDesk'));
const SamplesWorkspace = lazyWithRetry(() => import('../../pages/FieldService/SamplesWorkspace'));

// Form consumer pages — shared with P8 Forms module
const FormSelector = lazyWithRetry(() => import('../../pages/Forms/FormSelector'));
const FormFiller = lazyWithRetry(() => import('../../pages/Forms/FormFiller'));
const FormSubmissionList = lazyWithRetry(() => import('../../pages/Forms/FormSubmissionList'));
const FormSubmissionDetail = lazyWithRetry(() => import('../../pages/Forms/FormSubmissionDetail'));
const FormSubmissionEdit = lazyWithRetry(() => import('../../pages/Forms/FormSubmissionEdit'));
const FormReportPage = lazyWithRetry(() => import('../../pages/Forms/FormReportPage'));

/**
 * Wraps form consumer pages so navigation stays within /field-service/forms/*
 */
function FieldServiceFormsConsumer({ children }: { children: React.ReactNode }) {
  return (
    <FormConsumerProvider basePath="/field-service/forms" moduleLabel="Service Reports" moduleId="P11">
      {children}
    </FormConsumerProvider>
  );
}

export const routes: RouteObject[] = [
  {
    index: true,
    element: <ProtectedRoute><TicketsPage /></ProtectedRoute>,
  },
  {
    path: 'dashboard',
    element: <ProtectedRoute><FieldServiceDashboard /></ProtectedRoute>,
  },
  {
    path: 'tickets/:serviceId',
    element: <ProtectedRoute><ServiceTicketDetail /></ProtectedRoute>,
  },
  {
    // Calendar nav item now renders the technician week grid (SchedulerMatrix)
    // instead of the retired react-big-calendar month view.
    path: 'schedule',
    element: <ProtectedRoute><FieldServiceSchedulerPage /></ProtectedRoute>,
  },
  {
    // Old Tools > Scheduler URL — same page now lives at /field-service/schedule.
    path: 'scheduler',
    element: <Navigate to="/field-service/schedule" replace />,
  },
  {
    path: 'service-writing',
    element: <ProtectedRoute><ServiceWritingPage /></ProtectedRoute>,
  },
  {
    // Recurring exposes contract terms, recurring cost and billing rate per unit —
    // manager/admin only, mirroring the Owner/Admin/Manager gate firestore.rules puts
    // on `appsheet_recurring_schedules` (B3-RECURRING). The rule is the security
    // boundary; this guard is what stops a technician landing on a permanently-erroring
    // page instead of a clear "Access Denied".
    path: 'schedules',
    element: <ProtectedRoute requireManagerOrAdmin><RecurringProgramHealth /></ProtectedRoute>,
  },
  {
    // Same collection, same gate — the detail view shows the same financial fields.
    path: 'schedules/:scheduleId',
    element: <ProtectedRoute requireManagerOrAdmin><RecurringScheduleDetail /></ProtectedRoute>,
  },
  {
    path: 'generators',
    element: <ProtectedRoute><GeneratorsPage /></ProtectedRoute>,
  },
  {
    path: 'generators/:generatorId',
    element: <ProtectedRoute><GeneratorDetail /></ProtectedRoute>,
  },
  {
    path: 'schedule-blocks/:blockId',
    element: <ProtectedRoute><ScheduleBlockDetail /></ProtectedRoute>,
  },
  {
    path: 'parts-catalog/:partId',
    element: <ProtectedRoute><PartsCatalogDetail /></ProtectedRoute>,
  },
  {
    path: 'files/:fileId',
    element: <ProtectedRoute><ServiceFileDetail /></ProtectedRoute>,
  },
  {
    // Read-only extraction preview (E1, Task 11). Listed here, above the
    // `contracts/*` catch-all below, purely for readability -- React Router
    // v6 ranks static segments above dynamic/splat segments regardless of
    // array order, so the catch-all cannot actually swallow this route no
    // matter where it sits in the array. Saves nothing. Stays manager/admin only:
    // it shows the full extraction, which the rules keep manager-only.
    path: 'contracts/extract',
    element: <ProtectedRoute requireManagerOrAdmin><ContractExtractPage /></ProtectedRoute>,
  },
  {
    // Contract Library: the office contract readers (PR 5, Dillan 2026-10-02 --
    // Technician and Investor excluded), mirroring the `contracts` read rule.
    // Creating a contract stays manager-only (hidden on the page, refused by the server).
    path: 'contracts',
    element: (
      <ProtectedRoute allowRole={isOfficeContractReader}>
        <ContractsLibraryPage />
      </ProtectedRoute>
    ),
  },
  {
    // Contract page: same office reader gate. The page hides the manager-only
    // sections (worklist, Re-read, uploads, services/schedules editors) for the rest.
    path: 'contracts/:contractId',
    element: (
      <ProtectedRoute allowRole={isOfficeContractReader}>
        <ContractDetailPage />
      </ProtectedRoute>
    ),
  },
  {
    path: 'contracts/*',
    element: <Navigate to="/field-service/contracts" replace />,
  },
  // Existing FSR-specific pages (custom Field Service views)
  {
    path: 'reports',
    element: <ProtectedRoute><FSRReviewQueue /></ProtectedRoute>,
  },
  {
    path: 'recurring-review',
    element: <ProtectedRoute><RecurringReviewPage /></ProtectedRoute>,
  },
  {
    // Follow-up review queue (Service History P6): same gate as editing a ticket's Follow Up.
    path: 'follow-ups',
    element: <ProtectedRoute><FollowUpReviewPage /></ProtectedRoute>,
  },
  {
    // Photo-derived pin suggestions queue (GPS pinning). Same audience as pinning: any active
    // employee; the callable enforces it, the page explains it to anyone else.
    path: 'pin-suggestions',
    element: <ProtectedRoute><PinSuggestionsPage /></ProtectedRoute>,
  },
  {
    // Duplicate generators (ISS-499 A): read-only finder + the "not a duplicate" ruling. Manager/admin
    // only, mirroring the callables' P11 + Owner/Admin/Manager gate.
    path: 'duplicate-generators',
    element: <ProtectedRoute requireManagerOrAdmin><GeneratorDuplicatesPage /></ProtectedRoute>,
  },
  {
    // Fluid samples workspace: every sample, opening on what needs attention (PRD §9.5).
    // React Router ranks the static `samples/intake` above `samples`, so both coexist.
    path: 'samples',
    element: <ProtectedRoute><SamplesWorkspace /></ProtectedRoute>,
  },
  {
    // Fluid samples intake desk: replaces the paper sample log (docs/field-service/fluid-samples/PRD.md §9.3).
    path: 'samples/intake',
    element: <ProtectedRoute><SampleIntakeDesk /></ProtectedRoute>,
  },
  {
    path: 'needs-attention',
    element: <ProtectedRoute><NeedsAttentionPage /></ProtectedRoute>,
  },
  {
    // Ticket Owners grid: branch x stage -> default owner for confirmed handoffs. Read-only for
    // non-managers (the server reports canEdit and enforces it on every write).
    path: 'ticket-owners',
    element: <ProtectedRoute><TicketOwnersPage /></ProtectedRoute>,
  },
  {
    // Read-only bill view (Nick 2026-10-02). Creating, holding or marking billed is P4 only.
    path: 'billing-status',
    ...guardPage(BILL_VIEW_PAGES.fieldService, <ServiceBillingStatus />),
  },
  {
    // Consolidated into the FSR Review Queue (#2682). Redirect the old
    // "all reports" list — its status/branch/technician filters now live on
    // the single canonical page at /field-service/reports.
    path: 'reports/all',
    element: <Navigate to="/field-service/reports" replace />,
  },
  {
    path: 'reports/:submissionId',
    element: <ProtectedRoute><FSRReportPage /></ProtectedRoute>,
  },
  {
    // ONE archived GoFormz form, by its GoFormz UUID. The destination for the AppSheet
    // Service Files links being moved off GoFormz. The static `archive` segment is
    // deliberate: a bare `forms/:formId` could be shadowed by any future `forms/<word>`
    // route. See ArchivedFormPage's header. Not wrapped in FieldServiceFormsConsumer:
    // this is not a native form and must not pick up the form-filling nav context.
    path: 'forms/archive/:formId',
    element: <ProtectedRoute><ArchivedFormPage /></ProtectedRoute>,
  },
  // Plate grading (Init #2 truth B). UNLISTED (not in nav.ts): the server lets only the graders and
  // settlers Nick named use it; everyone else gets "not on the list". docs/plans/2026-09-29-plate-grading-in-app.md
  {
    path: 'plate-check',
    element: <ProtectedRoute><PlateCheckPage /></ProtectedRoute>,
  },
  {
    path: 'plate-check/settle',
    element: <ProtectedRoute><PlateSettlePage /></ProtectedRoute>,
  },
  // Form consumer routes — no P8 access required
  // These reuse the shared Forms pages with Field Service navigation context
  {
    path: 'forms/fill',
    element: <ProtectedRoute><FieldServiceFormsConsumer><FormSelector /></FieldServiceFormsConsumer></ProtectedRoute>,
  },
  {
    path: 'forms/fill/:templateId',
    element: <ProtectedRoute><FieldServiceFormsConsumer><FormFiller /></FieldServiceFormsConsumer></ProtectedRoute>,
  },
  {
    path: 'forms/submissions',
    element: <ProtectedRoute><FieldServiceFormsConsumer><FormSubmissionList /></FieldServiceFormsConsumer></ProtectedRoute>,
  },
  {
    path: 'forms/submissions/:submissionId',
    element: <ProtectedRoute><FieldServiceFormsConsumer><FormSubmissionDetail /></FieldServiceFormsConsumer></ProtectedRoute>,
  },
  {
    path: 'forms/submissions/:submissionId/edit',
    element: <ProtectedRoute><FieldServiceFormsConsumer><FormSubmissionEdit /></FieldServiceFormsConsumer></ProtectedRoute>,
  },
  {
    path: 'forms/submissions/:submissionId/report',
    element: <ProtectedRoute><FieldServiceFormsConsumer><FormReportPage /></FieldServiceFormsConsumer></ProtectedRoute>,
  },
];
